// The time axis down the left, and the footer text heatmap.py stamps in the
// bottom-left corner.

import { formatClock, type ClockFormat } from '../tz.ts'
import { formatDurationClock } from '../units.ts'
import { rowToMs, rowToY, timeTicks, timeToRow, type Clip, type View } from './layout.ts'

export const GUTTER_WIDTH = 78
const GUTTER_FONT = '11px ui-monospace, Menlo, Consolas, monospace'
const FOOTER_FONT = '11px ui-monospace, Menlo, Consolas, monospace'

export interface TimeTick {
  y: number
  text: string
}

/** heatmap.py's label format choice for a tick interval. */
export function clockFormatFor(stepSec: number): ClockFormat {
  if (stepSec % 86400 === 0) return 'date'
  if (stepSec % 60 === 0) return 'hm'
  return 'hms'
}

export function layoutTimeTicks(
  clip: Clip,
  view: View,
  heightPx: number,
  displayZone: string,
  ytickSec: number | null,
): TimeTick[] {
  const startMs = rowToMs(clip, view.row0)
  const endMs = rowToMs(clip, view.row1)
  const { stepSec, ticks } = timeTicks(startMs, endMs, displayZone, ytickSec, heightPx, 44)
  const format = clockFormatFor(stepSec)
  const out: TimeTick[] = []
  let lastY = -Infinity
  for (const t of ticks) {
    const y = rowToY(view, timeToRow(clip, t), heightPx)
    if (y < 0 || y > heightPx) continue
    // heatmap.py skips a label within 15 px of the previous one.
    if (y - lastY <= 15) continue
    lastY = y
    out.push({ y, text: formatClock(t, displayZone, format) })
  }
  return out
}

export function drawGutter(
  ctx: CanvasRenderingContext2D,
  ticks: TimeTick[],
  widthPx: number,
  heightPx: number,
  scale: number,
): void {
  ctx.save()
  ctx.scale(scale, scale)
  ctx.fillStyle = '#16161c'
  ctx.fillRect(0, 0, widthPx, heightPx)
  ctx.font = GUTTER_FONT
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'right'
  for (const tick of ticks) {
    ctx.strokeStyle = '#4a4a55'
    ctx.beginPath()
    ctx.moveTo(widthPx - 6, tick.y + 0.5)
    ctx.lineTo(widthPx, tick.y + 0.5)
    ctx.stroke()
    ctx.fillStyle = '#c8c8d0'
    ctx.fillText(tick.text, widthPx - 9, tick.y)
  }
  ctx.restore()
}

export interface FooterLines {
  duration: string
  range: string
  pixel: string
  started: string
}

/** The four lines heatmap.py writes in the corner, for the clip as a whole. */
export function footerLines(clip: Clip, displayZone: string): FooterLines {
  const { run } = clip
  const step = run.geometry.stepHz
  const minHz = run.binHz[clip.bin0]! + clip.offsetHz
  const maxHz = run.binHz[clip.bin1 - 1]! + clip.offsetHz
  const durationSec = (clip.endMs - clip.startMs + run.cadenceMs) / 1000
  const rowSec = clip.compressed ? (durationSec / clip.rows) : run.cadenceMs / 1000
  return {
    duration: `Duration: ${formatDurationClock(durationSec)}`,
    range: `Range: ${(minHz / 1e6).toFixed(2)}MHz - ${((maxHz + step) / 1e6).toFixed(2)}MHz`,
    pixel: `Pixel: ${step.toFixed(2)}Hz x ${Math.round(rowSec)}s`,
    started: `Started: ${formatClock(clip.startMs, displayZone, 'full')}`,
  }
}

/** heatmap.py's shadow_text: black one pixel down-right, white on top. */
export function drawShadowText(ctx: CanvasRenderingContext2D, x: number, y: number, text: string): void {
  ctx.font = FOOTER_FONT
  ctx.textBaseline = 'top'
  ctx.textAlign = 'left'
  ctx.fillStyle = '#000'
  ctx.fillText(text, x + 1, y + 1)
  ctx.fillStyle = '#fff'
  ctx.fillText(text, x, y)
}

export function drawFooter(ctx: CanvasRenderingContext2D, lines: FooterLines, x: number, bottomY: number): void {
  drawShadowText(ctx, x, bottomY - 45, lines.duration)
  drawShadowText(ctx, x, bottomY - 35, lines.range)
  drawShadowText(ctx, x, bottomY - 25, lines.pixel)
  drawShadowText(ctx, x, bottomY - 15, lines.started)
}
